// [2026-08-07] ADDED — the shell: session, navigation, and the shared time range.
//
// NAVIGATION IS STATE, NOT A ROUTER.
// The dashboard is served at "/" by a FastAPI app that has no catch-all route (app/admin_server.py
// only serves "/" and "/static"). A history-API router would give every screen a URL that 404s the
// moment anyone reloads or shares it. Keeping the view in state means every URL that exists works,
// and nothing pretends to be linkable when it is not.
//
// THE TIME RANGE IS SHARED AND LIVES HERE.
// One control at the top of the shell, applied to every panel. Per-screen ranges are how two
// charts end up silently showing different windows while sitting side by side.
//
// WHAT THE TWO AUDIENCES SEE.
// A customer (`client` role) is scoped to their own client_id by the API on every single handler —
// hiding the admin nav below is convenience, NOT the security boundary. The boundary is
// scoped_client_id() in app/modules/admin/route.py, which resolves the tenant from the signed
// session and ignores anything the browser sends.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api, getToken, setToken } from './api.js'
// [2026-08-11] ADDED — the sign-in screen's presentation. Logic stays in `Login` below.
import LoginScreen from './components/auth/LoginScreen.jsx'
import { Notice, RangePicker, Skeleton, ToastHost, toast } from './ui.jsx'

// [2026-08-27] CHANGED — this repository is now the CUSTOMER app only. The five InCruiter-staff
// screens moved out to proctoring-admin-frontend, which depends on this package and re-uses every
// view below rather than copying it.
//
// So this file no longer imports or renders anything admin. `isAdmin` still exists further down,
// because it is NOT a routing switch — it widens what two customer screens display (a client
// column on Candidates, a broader hint on Upload) and labels the sidebar footer. It grants
// nothing; the real boundary is server-side (require_admin / scoped_client_id).
//
// OLD (single app, admin screens gated by isAdmin):
//   import AdminHealth from './views/admin/AdminHealth.jsx'
//   import { AdminAudit, AdminClients, AdminPlatform, AdminSessions } from './views/admin/Admin.jsx'
// and, before the client/ + admin/ folder split:
//   import AdminHealth from './views/AdminHealth.jsx'   ... etc, all from './views/'

// ── customer screens. Also exported from src/index.js so the admin app can render them. ──
import Overview from './views/Overview.jsx'
import Analytics from './views/Analytics.jsx'
import Credits from './views/Credits.jsx'
import Recordings from './views/Recordings.jsx'
import Keys from './views/Keys.jsx'
// [2026-08-10] ADDED — candidate reports from the browser SDK.
import Candidates from './views/Candidates.jsx'
import Upload from './views/Upload.jsx'

const ICONS = {
  upload: <path d="M12 16V4M7 9l5-5 5 5M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" />,
  overview: <><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" />
             <rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></>,
  analytics: <path d="M3 3v18h18M7 15l4-4 3 3 5-6" />,
  credits: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>,
  recordings: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M10 9l5 3-5 3z" /></>,
  keys: <><circle cx="8" cy="12" r="4" /><path d="M12 12h9M18 12v4" /></>,
  // [2026-08-10] ADDED — a person outline: this screen is about candidates, not files.
  candidates: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  platform: <><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="8" />
              <rect x="13" y="6" width="3" height="12" /></>,
  clients: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
             <path d="M23 21v-2a4 4 0 00-3-3.87" /></>,
  audit: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <path d="M14 2v6h6M9 15h6" /></>,
  signout: <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />,
  // [2026-08-11] ADDED — a pulse line, for System health.
  health: <path d="M3 12h4l3-8 4 16 3-8h4" />,
}

const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">{ICONS[name]}</svg>
)

const VIEWS = {
  upload:     { title: 'New analysis',   blurb: 'Upload a recording and run the proctoring pipeline on it.' },
  overview:   { title: 'Overview',       blurb: 'Volume, reliability and spend at a glance.' },
  analytics:  { title: 'Usage & analytics', blurb: 'Latency, traffic shape and per-endpoint behaviour.' },
  credits:    { title: 'Credits',        blurb: 'Balance, ledger and what a recording will cost.' },
  recordings: { title: 'Recordings',     blurb: 'Every recording submitted for proctoring analysis.' },
  keys:       { title: 'API keys',       blurb: 'Credentials and the analyses enabled on this account.' },
  // [2026-08-10] ADDED — the browser SDK's own reports. Deliberately worded to distinguish it
  // from Recordings: that screen is the video, this one is what the candidate's browser did.
  candidates: { title: 'Candidate reports',
                blurb: 'Live browser activity captured by the SDK, beside the video analysis of the same candidate.' },
  'admin-platform': { title: 'Platform', blurb: 'Every client, across the whole deployment.' },
  'admin-clients':  { title: 'Clients',  blurb: 'Accounts, balances and service toggles.' },
  'admin-sessions': { title: 'All analyses', blurb: 'Analysis jobs across every client.' },
  'admin-audit':    { title: 'Audit log', blurb: 'Who changed what, and when.' },
  // [2026-08-11] ADDED — the 3am screen: which dependency is down, which endpoint is failing, and
  // which features are actually switched on in this environment.
  // [2026-08-11] Named "Diagnostics", NOT "System health" — the Overview screen already has a
  // panel by that name (persistence backend, Mongo, Redis). Two screens sharing a name is
  // ambiguous for a reader and, as the dashboard e2e proved, for a locator too.
  'admin-health':   { title: 'Diagnostics',
                      blurb: 'Dependencies, endpoint error rates and the flags this environment is running with.' },
}

// [2026-08-08] ADDED — deep-link the opening screen with `?view=keys`.
//
// WHY A QUERY PARAM AND NOT A PATH. The comment at the top of this file explains that the view is
// held in state precisely because app/admin_server.py serves only "/" and "/static" — a path like
// /keys would 404 on reload or on a shared link. A QUERY STRING does not change the path, so "/"
// still serves index.html and every existing URL keeps working exactly as before.
//
// This exists so the landing site can send someone straight to their API keys instead of dropping
// them on Overview with "now find it yourself". Unknown or absent value => 'overview', i.e. the
// behaviour before this change. Nothing else reads the URL.
// Admin screens (`admin-*`) are deliberately NOT reachable this way. The real boundary is
// scoped_client_id() server-side, so a customer landing on one would see only their own data
// anyway — but a URL that drops a customer on a screen labelled "Platform" is a support ticket,
// not a feature. Operators reach those from the sidebar, which is where they already are.
function initialView() {
  try {
    const requested = new URLSearchParams(window.location.search).get('view')
    const allowed = requested && VIEWS[requested] && !requested.startsWith('admin-')
    return allowed ? requested : 'overview'
  } catch {
    // A malformed URL must never stop the dashboard from booting.
    return 'overview'
  }
}

// [2026-08-27] CHANGED — App now takes two OPTIONAL extension points so that
// proctoring-admin-frontend can reuse this entire shell (sidebar, session handling, range picker,
// toast host, 401 recovery) instead of copying 440 lines of it.
//
// Both default to null, so the customer app renders EXACTLY as it did before — this is additive.
//
//   extraNav({ view, setView, isAdmin })     -> extra sidebar groups, appended after "Dashboard"
//   extraViews({ view, days, onError, isAdmin }) -> extra screens, appended after the customer ones
//
// They are render props, not children, because both need `view`/`setView` from this component's
// state and the admin app must not own a second copy of that state.
//
// OLD: export default function App() {
export default function App({ extraNav = null, extraViews = null }) {
  const [session, setSession] = useState(null)
  const [booting, setBooting] = useState(true)
  // [2026-08-08] CHANGED — seeded from the URL. OLD: useState('overview')
  // [2026-08-28] CHANGED — renamed to `rawView`; the value the rest of this component uses is
  // derived below, once `isAdmin` is known, so a customer arriving on `?view=keys` is put on
  // Overview rather than on a blank screen headed "API keys". Purely local to this component —
  // `setView` is unchanged and nothing outside this file ever saw this name.
  //
  // [2026-08-28] OLD:  const [view, setView] = useState(initialView)
  const [rawView, setView] = useState(initialView)
  const [days, setDays] = useState(30)

  // Resume an existing session on load rather than forcing a sign-in on every refresh.
  useEffect(() => {
    if (!getToken()) { setBooting(false); return }
    api.me()
      .then((user) => { setSession(user); setBooting(false) })
      .catch(() => { setToken(null); setBooting(false) })
  }, [])

  const signOut = useCallback(() => {
    setToken(null)
    setSession(null)
    setView('overview')
  }, [])

  // A 401 anywhere means the session died mid-use. Drop to the login screen once, centrally,
  // rather than letting each panel render its own "unauthorised" error.
  const onError = useCallback((err) => {
    if (err instanceof ApiError && err.code === 'UNAUTHORISED') {
      signOut()
      toast('Your session expired. Please sign in again.', true)
    }
  }, [signOut])

  if (booting) {
    return <div className="login-wrap"><Skeleton height={120} style={{ width: 380 }} /></div>
  }
  if (!session) return <><Login onSignedIn={setSession} /><ToastHost /></>

  const isAdmin = session.role === 'incruiter_admin'

  // [2026-08-28] ADDED — the API keys screen is operator-only now. API keys are issued by an
  // InCruiter operator and handed over manually, so a customer has no self-serve key screen.
  //
  // WHY THIS IS DERIVED RATHER THAN GATED AT THE RENDER SITE. `initialView()` reads `?view=keys`
  // from the URL and runs BEFORE the session exists, so it cannot know the role. Gating only the
  // nav item and the render would leave a customer who follows an old landing-site link on a blank
  // page headed "API keys" — the title comes from `meta`, which is computed from this value. One
  // derivation fixes the nav highlight, the heading and the body together.
  //
  // THIS IS NOT A SECURITY BOUNDARY and must not be mistaken for one — the bundle still contains
  // Keys.jsx and anyone can read it. The real control is server-side and already in place:
  // GATEWAY_SELF_SERVE_KEYS_ENABLED defaults to FALSE (config.py:774), so the mint endpoint refuses
  // a customer regardless of what the browser renders.
  const view = (rawView === 'keys' && !isAdmin) ? 'overview' : rawView

  const meta = VIEWS[view] || { title: '', blurb: '' }

  return (
    <div className="app">
      <aside className="sidebar">
        {/* [2026-08-07] CHANGED — the brand reads "InCruiter", not a "P" mark and "Proctoring".
            The dashboard is shown to customers, and the product is the company's name.
            OLD: <span className="brand-mark">P</span><span>Proctoring</span> */}
        <div className="brand"><span className="brand-mark">In</span><span>InCruiter</span></div>

        <NavGroup label="Analyse">
          <NavItem id="upload" icon="upload" view={view} setView={setView}>New analysis</NavItem>
        </NavGroup>

        <NavGroup label="Dashboard">
          <NavItem id="overview" icon="overview" view={view} setView={setView}>Overview</NavItem>
          <NavItem id="analytics" icon="analytics" view={view} setView={setView}>Usage &amp; analytics</NavItem>
          <NavItem id="credits" icon="credits" view={view} setView={setView}>Credits</NavItem>
          <NavItem id="recordings" icon="recordings" view={view} setView={setView}>Recordings</NavItem>
          {/* [2026-08-10] ADDED — sits beside Recordings because the two are the same question
              asked of different evidence: the video, and the browser. */}
          <NavItem id="candidates" icon="candidates" view={view} setView={setView}>Candidate reports</NavItem>
          {/* [2026-08-28] CHANGED — operator-only. Keys are issued by InCruiter and handed to the
              customer manually, so there is no customer-facing key screen. The NavItem, the icon,
              the VIEWS entry and Keys.jsx all stay — removing the `isAdmin &&` restores it.
              [2026-08-28] OLD:
              <NavItem id="keys" icon="keys" view={view} setView={setView}>API keys</NavItem> */}
          {isAdmin && (
            <NavItem id="keys" icon="keys" view={view} setView={setView}>API keys</NavItem>
          )}
        </NavGroup>

        {/* [2026-08-27] REMOVED HERE, MOVED — not deleted. The InCruiter-staff nav group now lives
            in proctoring-admin-frontend/src/App.jsx, which renders this same sidebar and appends it.
            OLD:
              {isAdmin && (
                <NavGroup label="InCruiter admin">
                  <NavItem id="admin-platform" …>Platform</NavItem>
                  <NavItem id="admin-clients" …>Clients</NavItem>
                  <NavItem id="admin-sessions" …>All analyses</NavItem>
                  <NavItem id="admin-audit" …>Audit log</NavItem>
                  <NavItem id="admin-health" …>Diagnostics</NavItem>
                </NavGroup>
              )} */}
        {extraNav && extraNav({ view, setView, isAdmin })}

        <div className="sidebar-foot">
          <div style={{ padding: '0 8px 10px' }}>
            <div className="who-email">{session.email}</div>
            <div className="muted" style={{ fontSize: 11.5 }}>
              {isAdmin ? 'InCruiter admin' : (session.client_id || 'Client')}
            </div>
          </div>
          <button className="nav-item" onClick={signOut}><Icon name="signout" />Sign out</button>
        </div>
      </aside>

      <main className="main">
        <div className="page-head">
          <div>
            <h1>{meta.title}</h1>
            <p>{meta.blurb}</p>
          </div>
          {/* The upload screen takes no time range — it is an action, not a report. */}
          {view !== 'upload' && view !== 'keys' && (
            <RangePicker value={days} onChange={setDays} />
          )}
        </div>

        {view === 'upload' && <Upload isAdmin={isAdmin} onError={onError} onDone={() => setView('recordings')} />}
        {view === 'overview' && <Overview days={days} onError={onError} />}
        {view === 'analytics' && <Analytics days={days} onError={onError} />}
        {view === 'credits' && <Credits days={days} onError={onError} />}
        {view === 'recordings' && <Recordings onError={onError} />}
        {/* [2026-08-28] CHANGED — operator-only, matching the nav item above. The `isAdmin` here is
            belt-and-braces: `view` can no longer BE 'keys' for a customer (see the derivation
            above), so this guard is redundant today and kept deliberately — if someone later
            removes that derivation, this still stops a customer rendering the screen.
            [2026-08-28] OLD:  {view === 'keys' && <Keys onError={onError} />} */}
        {view === 'keys' && isAdmin && <Keys onError={onError} />}
        {/* [2026-08-10] ADDED. `isAdmin` only widens what the SCREEN shows (a client column, a
            broader search hint) — it grants nothing. The API decides what an operator may read,
            from the signed session, in _browser_scope(). */}
        {view === 'candidates' && <Candidates days={days} onError={onError} isAdmin={isAdmin} />}
        {/* [2026-08-27] REMOVED HERE, MOVED — the five staff screens render in
            proctoring-admin-frontend now. Their VIEWS entries above are deliberately kept so the
            title/blurb metadata has exactly one definition, which the admin app imports.
            OLD:
              {view === 'admin-platform' && <AdminPlatform days={days} onError={onError} />}
              {view === 'admin-clients'  && <AdminClients  days={days} onError={onError} />}
              {view === 'admin-sessions' && <AdminSessions onError={onError} />}
              {view === 'admin-audit'    && <AdminAudit    onError={onError} />}
              {view === 'admin-health'   && <AdminHealth   onError={onError} />} */}
        {extraViews && extraViews({ view, days, onError, isAdmin })}
      </main>

      <ToastHost />
    </div>
  )
}

// [2026-08-27] CHANGED — exported so proctoring-admin-frontend renders its sidebar group with the
// SAME components as the customer groups. If they diverge, the staff group stops matching the rest
// of the sidebar and e2e locators that walk the nav break.
// OLD: function NavGroup({ label, children }) {
export function NavGroup({ label, children }) {
  return <div><div className="nav-label">{label}</div>{children}</div>
}

// [2026-08-27] CHANGED — exported for the same reason as NavGroup above.
// OLD: function NavItem({ id, icon, view, setView, children }) {
export function NavItem({ id, icon, view, setView, children }) {
  return (
    <button className={`nav-item ${view === id ? 'active' : ''}`} onClick={() => setView(id)}>
      <Icon name={icon} />{children}
    </button>
  )
}

// [2026-08-07] CHANGED — two distinct doors plus Sign in with Google.
//
// TWO DOORS, ONE BOUNDARY. The Client / InCruiter staff switch changes what the screen SAYS and
// where you land, and it refuses a sign-in whose role does not match the door you picked. It is a
// usability feature, NOT a security control: the real boundary is the signed session and
// require_admin() on every staff handler server-side. Picking "InCruiter staff" cannot give a
// customer account admin rights — it just tells them they chose the wrong door.
//
// GOOGLE IS PRE-CREATED-USERS-ONLY, enforced server-side (app/modules/admin/route.py). The button
// only appears when the deployment has configured it, so a half-configured install shows a
// password form rather than a button that always fails.

// [2026-08-11] CHANGED — added `heading`, a short form-column title for the redesigned sign-in
// screen. `title` is KEPT and unchanged: it is the long product name and the two are not
// interchangeable — "InCruiter Proctoring — Admin" reads as a page banner, not as the sentence
// above a pair of input fields.
// [2026-08-27] CHANGED — the door is decided by the URL, not by a toggle on a shared page.
//
// `/`       the CUSTOMER sign-in. Password + Google + Create account + Forgot password.
// `/admin`  the STAFF sign-in.    Password only, @incruiter.com only. No Google, no signup,
//                                 no reset — see config.py:GOOGLE_AUTH_CLIENT_DOOR_ONLY for why
//                                 the highest-privilege account deliberately has FEWER ways in.
//
// THE TOGGLE IS GONE. A customer must never be shown a staff door at all, which was the whole
// point of separating them; a segmented control on one page is the opposite of that.
//
// STILL NOT A SECURITY BOUNDARY — and this is worth repeating because two URLs look even more like
// one than a toggle did. Anyone can open /admin. The real controls are all server-side: the signed
// session, `require_admin()` on every staff handler, and now `_enforce_door()`, which refuses a
// customer credential POSTed at the staff door regardless of what any page renders.
//
// --- OLD (pre-2026-08-27): one page, `door` in state, a Client / InCruiter staff toggle ---
// const DOORS = {
//   client: {
//     label: 'Client',
//     title: 'InCruiter Proctoring',
//     heading: 'Welcome back',
//     blurb: 'Sign in to your proctoring dashboard',
//     role: 'client_admin',
//     wrongRole: 'That account is an InCruiter staff login. Switch to the InCruiter staff tab.',
//   },
//   admin: {
//     label: 'InCruiter staff',
//     title: 'InCruiter Proctoring — Admin',
//     heading: 'Staff sign-in',
//     blurb: 'Platform administration and client management',
//     role: 'incruiter_admin',
//     wrongRole: 'That account is a client login. Switch to the Client tab.',
//   },
// }
// ...and `const [door, setDoor] = useState('client')` with an `onDoorChange` prop.
// --- END OLD ---
const DOORS = {
  client: {
    label: 'Client',
    title: 'InCruiter Proctoring',
    heading: 'Welcome back',
    blurb: 'Sign in to your proctoring dashboard',
    role: 'client_admin',
    // Kept as copy for the case where a STAFF credential is typed into the customer page. The
    // server refuses it too; this is the friendlier local message.
    wrongRole: 'That is an InCruiter staff account. Please use the staff sign-in page.',
  },
  admin: {
    label: 'InCruiter staff',
    title: 'InCruiter Proctoring — Admin',
    heading: 'Staff sign-in',
    blurb: 'Platform administration and client management',
    role: 'incruiter_admin',
    wrongRole: 'That is a client account. Please use the client sign-in page.',
  },
}

/** The reset token from `?reset_token=…`, if the person arrived from a reset email.
 *
 *  A QUERY PARAM, not a path, for the same reason every other deep link in this app is one: the
 *  server serves only `/`, `/admin` and `/static`, so `/reset/abc` would 404 on the very click the
 *  email exists to make work (see proctoring-dashboard/CLAUDE.md). */
export function resetTokenFromLocation() {
  try {
    return new URLSearchParams(window.location.search).get('reset_token') || null
  } catch {
    return null
  }
}

/** Which door this page is. Read ONCE from the path — there is no way to switch at runtime.
 *
 *  Anything that is not exactly the admin path is the customer door. Failing toward the CUSTOMER
 *  page is the safe direction: the worst case is a staff member on the wrong page, who is told so
 *  and clicks through. Failing toward the staff page would show the staff door to everyone. */
export function doorFromLocation() {
  try {
    const path = window.location.pathname.replace(/\/+$/, '')
    return path === '/admin' ? 'admin' : 'client'
  } catch {
    return 'client'
  }
}

function Login({ onSignedIn }) {
  // Not state: the door is a property of the URL, and making it state would re-introduce exactly
  // the runtime switching this change removes.
  const door = doorFromLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [providers, setProviders] = useState(null)
  // [2026-08-27] ADDED — which panel the customer page is showing: sign-in, forgot, or reset.
  // Seeded from `?reset_token=`, so the link in the reset email lands directly on the new-password
  // form instead of on a sign-in page the person cannot get past.
  const [panel, setPanel] = useState(() => (resetTokenFromLocation() ? 'reset' : 'signin'))
  const [notice, setNotice] = useState(null)
  // [2026-08-27] ADDED — the reset form asks for the password twice. A mistyped password that is
  // only entered once locks the person out of the account they were in the middle of recovering.
  const [confirmPassword, setConfirmPassword] = useState('')
  const googleRef = useRef(null)

  const meta = DOORS[door]

  useEffect(() => {
    // Public endpoint — it is called before anyone is signed in and returns only the Google client
    // id, which is public by design. Asked PER DOOR so the staff page is told Google is off.
    api.providers(door)
      .then(setProviders)
      .catch(() => setProviders({ password: true, google: { enabled: false } }))
  }, [door])

  // Accept a signed-in user only if their role matches this page's door.
  //
  // The server enforces this too (`_enforce_door`), so this is a nicety that produces a clearer
  // message — NOT the control. It is kept because a local check can name the other page without a
  // round trip, and because a token that arrives with the wrong role must be discarded rather than
  // stored while we decide what to do with it.
  const accept = useCallback((result) => {
    if (result.user?.role !== meta.role) {
      setToken(null)
      setError(meta.wrongRole)
      return
    }
    setToken(result.token)
    onSignedIn(result.user)
  }, [meta.role, meta.wrongRole, onSignedIn])

  // ── Google Identity Services ──────────────────────────────────────────────
  useEffect(() => {
    const google = providers?.google
    if (!google?.enabled || !google.client_id) return

    let cancelled = false
    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !googleRef.current) return
      window.google.accounts.id.initialize({
        client_id: google.client_id,
        callback: async (response) => {
          setBusy(true)
          setError(null)
          try {
            // [2026-08-27] CHANGED — try sign-in, and fall back to SIGN-UP when this deployment
            // allows self-registration and the account does not exist yet.
            //
            // OLD:
            //   accept(await api.googleLogin(response.credential))
            //
            // WHY ONE BUTTON AND NOT TWO. Google renders its own button and it cannot be
            // restyled or duplicated convincingly; showing "Sign in with Google" beside "Sign up
            // with Google" forces the person to know which one they are, and they frequently do
            // not. One button that does the right thing is the honest interface.
            //
            // The ORDER matters: sign-in is attempted FIRST, so an existing account never takes
            // the signup path. Signup is only reached on the specific "not registered" refusal.
            try {
              accept(await api.googleLogin(response.credential, door))
            } catch (err) {
              const unregistered = err?.code === 'PORTAL_UNAUTHORISED'
              if (!providers?.self_registration || !unregistered) throw err
              accept(await api.googleSignup(response.credential, null))
            }
          } catch (err) {
            setError(err.message)
          } finally {
            setBusy(false)
          }
        },
      })
      googleRef.current.innerHTML = ''
      window.google.accounts.id.renderButton(googleRef.current, {
        theme: 'outline', size: 'large', width: 320,
        // "continue_with" rather than "signin_with", because this button now does both.
        text: providers?.self_registration ? 'continue_with' : 'signin_with',
      })
    }

    if (window.google?.accounts?.id) { render(); return () => { cancelled = true } }

    // Loaded on demand rather than in index.html, so a deployment with Google switched off never
    // makes a request to Google at all.
    const existing = document.getElementById('gsi-script')
    const script = existing || Object.assign(document.createElement('script'), {
      id: 'gsi-script', src: 'https://accounts.google.com/gsi/client', async: true, defer: true,
    })
    script.addEventListener('load', render)
    if (!existing) document.head.appendChild(script)
    return () => { cancelled = true; script.removeEventListener('load', render) }
  }, [providers, accept])

  async function submit(event) {
    event.preventDefault()
    // [2026-08-27] ADDED — validate before spending a network round trip and, more importantly,
    // before the server spends a 600k-iteration PBKDF2 on an obviously-empty submission
    // (app/core/portal_auth.py:52 — it runs the KDF even for an unknown email, to keep timing
    // constant). `required` on the inputs covers the normal path; this covers whitespace-only.
    if (!email.trim() || !password) {
      setError('Enter your email address and password.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      // [2026-08-27] CHANGED — send the door, so the server can refuse a credential presented at
      // the wrong sign-in page. OLD: accept(await api.login(email, password))
      accept(await api.login(email.trim(), password, door))
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // [2026-08-27] ADDED — request a reset link.
  //
  // The response is IDENTICAL whether or not the address has an account (the server guarantees
  // this — see the handler), and this UI must not undo that guarantee by rendering a different
  // message in the two cases. So it always shows the same neutral confirmation.
  async function submitForgot(event) {
    event.preventDefault()
    const address = email.trim()
    if (!address || !address.includes('@')) {
      setError('Enter the email address you sign in with.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.forgotPassword(address)
      setPanel('forgot_sent')
      setNotice('If that email has an account, a reset link is on its way. '
                + 'Check your inbox — the link is valid for 30 minutes.')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // [2026-08-27] ADDED — set a new password from a reset link.
  async function submitReset(event) {
    event.preventDefault()
    // Mirrors the server's own rule (ResetPasswordRequest: min_length=8), so the person is told
    // the real constraint here instead of receiving a 422 after submitting.
    if (password.length < 8) {
      setError('Choose a password of at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.resetPassword(resetTokenFromLocation(), password)
      // Deliberately does NOT sign them in — the server returns no token, so that knowing the link
      // is not the same as being signed in. They sign in with the password they just chose, which
      // also proves they know it.
      setPassword('')
      setConfirmPassword('')
      setPanel('signin')
      setNotice('Your password has been updated. Please sign in.')
      // Strip the spent token from the address bar so a reload does not land back on a form whose
      // link is now dead, and so the token does not sit in browser history any longer than needed.
      try {
        window.history.replaceState({}, '', window.location.pathname)
      } catch { /* a locked-down browser may refuse; the flow still completed */ }
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const googleReady = providers?.google?.enabled

  // [2026-08-11] CHANGED — the markup moved to components/auth/LoginScreen.jsx, a two-panel
  // shadcn layout with the artwork on the left and the form on the right. ALL BEHAVIOUR ABOVE THIS
  // LINE IS UNTOUCHED: the door/role check, the Google Identity Services lifecycle and `submit`
  // are exactly as they were, and the new file receives them as props. Keeping the logic here is
  // the point of the split — the presentation is what gets redesigned, and a redesign must not be
  // able to reach the authentication path.
  //
  // --- OLD (pre-2026-08-11): single centred .login-card ---
  // return (
  //   <div className="login-wrap">
  //     <div className="login-card">
  //       <div className="brand" style={{ padding: '0 0 4px' }}>
  //         <span className="brand-mark">In</span><span>{meta.title}</span>
  //       </div>
  //       <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>{meta.blurb}</p>
  //       <div className="seg" style={{ display: 'flex', width: '100%', marginBottom: 16 }}>
  //         {Object.entries(DOORS).map(([key, value]) => (
  //           <button key={key} type="button" style={{ flex: 1 }}
  //                   className={door === key ? 'active' : ''}
  //                   onClick={() => { setDoor(key); setError(null) }}>
  //             {value.label}
  //           </button>
  //         ))}
  //       </div>
  //       {error && <Notice kind="error">{error}</Notice>}
  //       <form onSubmit={submit}>
  //         <label className="field">
  //           <span>Email</span>
  //           <input type="email" value={email} required autoFocus autoComplete="username"
  //                  onChange={(e) => setEmail(e.target.value)} />
  //         </label>
  //         <label className="field">
  //           <span>Password</span>
  //           <input type="password" value={password} required autoComplete="current-password"
  //                  onChange={(e) => setPassword(e.target.value)} />
  //         </label>
  //         <button className="btn" type="submit" disabled={busy}
  //                 style={{ width: '100%', justifyContent: 'center' }}>
  //           {busy ? 'Signing in…' : `Sign in${door === 'admin' ? ' as staff' : ''}`}
  //         </button>
  //       </form>
  //       {googleReady && (
  //         <>
  //           <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 14px' }}>
  //             <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
  //             <span className="muted" style={{ fontSize: 11.5 }}>OR</span>
  //             <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
  //           </div>
  //           <div ref={googleRef} style={{ display: 'flex', justifyContent: 'center' }} />
  //           <p className="muted" style={{ margin: '10px 0 0', textAlign: 'center', fontSize: 11.5 }}>
  //             Google sign-in works only for accounts already registered for the dashboard.
  //           </p>
  //         </>
  //       )}
  //       <p className="muted" style={{ margin: '14px 0 0', textAlign: 'center', fontSize: 12.5 }}>
  //         Trouble signing in? Contact your InCruiter account manager.
  //       </p>
  //     </div>
  //   </div>
  // )
  // --- END OLD ---
  // [2026-08-27] CHANGED — `doors`/`onDoorChange` are GONE (the toggle no longer exists) and the
  // panel machinery, notice and reset fields are new. Every piece of behaviour still lives here;
  // LoginScreen stays presentation-only, which is the point of the split.
  return (
    <LoginScreen
      door={door}
      meta={meta}
      panel={panel}
      onPanelChange={(next) => { setPanel(next); setError(null); setNotice(null) }}
      notice={notice}
      email={email}
      onEmailChange={setEmail}
      password={password}
      onPasswordChange={setPassword}
      confirmPassword={confirmPassword}
      onConfirmPasswordChange={setConfirmPassword}
      error={error}
      busy={busy}
      onSubmit={submit}
      onSubmitForgot={submitForgot}
      onSubmitReset={submitReset}
      googleReady={googleReady}
      googleRef={googleRef}
      selfRegistration={Boolean(providers?.self_registration)}
      passwordReset={Boolean(providers?.password_reset)}
    />
  )
}
